import { formatDateTime, formatPhone, fullName } from '@hr/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { UsersIcon } from 'lucide-react';
import { useMemo } from 'react';
import { z } from 'zod';
import { DataTable } from '@/components/data-table';
import { EmptyState, PageHeader } from '@/components/page';
import { RepeatBadge } from '@/components/repeat-badge';
import { SearchInput } from '@/components/search-input';
import { Checkbox } from '@/components/ui/checkbox';
import { uk } from '@/i18n/uk';
import { useOverlays } from '@/lib/overlays';
import { listSearchShape, tableState, tableStateToSearch } from '@/lib/search-params';
import { trpc } from '@/lib/trpc';
import type { CandidateListItem } from '@/lib/types';

const searchSchema = z.object({
  ...listSearchShape,
  repeat: z.boolean().optional().catch(undefined),
  city: z.string().max(100).optional().catch(undefined),
});

export const Route = createFileRoute('/_app/candidates/')({
  validateSearch: searchSchema,
  component: CandidatesPage,
});

function CandidatesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const overlays = useOverlays();
  const state = tableState(search, { sort: 'createdAt', dir: 'desc' });
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const list = useQuery({
    ...trpc.candidates.list.queryOptions({
      page: state.page,
      pageSize: state.pageSize,
      sort: state.sort,
      dir: state.dir,
      q: search.q,
      repeatOnly: search.repeat,
      city: search.city,
    }),
    placeholderData: keepPreviousData,
  });

  const columns = useMemo<ColumnDef<CandidateListItem>[]>(
    () => [
      {
        id: 'name',
        header: uk.candidates.fullName,
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{fullName(row.original)}</span>
            <RepeatBadge candidateId={row.original.id} count={row.original.applicationsCount} />
          </div>
        ),
        meta: { className: 'min-w-64' },
      },
      {
        id: 'phone',
        header: uk.candidates.phone,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.phoneE164 ? (
            formatPhone(row.original.phoneE164)
          ) : (
            <span className="text-muted-foreground">{row.original.phoneRaw}</span>
          ),
        meta: { className: 'whitespace-nowrap' },
      },
      { id: 'email', header: uk.candidates.email, enableSorting: false, accessorKey: 'email' },
      { id: 'city', header: uk.candidates.city, accessorKey: 'city' },
      { id: 'applications', header: uk.candidates.applications, accessorKey: 'applicationsCount' },
      {
        id: 'createdAt',
        header: uk.candidates.createdAt,
        cell: ({ row }) => formatDateTime(row.original.createdAt),
        meta: { className: 'whitespace-nowrap' },
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader title={uk.candidates.title} />
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <SearchInput
          className="w-80"
          value={search.q ?? ''}
          placeholder={uk.candidates.searchPlaceholder}
          onChange={(q) => setSearch({ q: q || undefined, page: undefined })}
        />
        <SearchInput
          className="w-44"
          value={search.city ?? ''}
          placeholder={uk.candidates.city}
          onChange={(city) => setSearch({ city: city || undefined, page: undefined })}
        />
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={Boolean(search.repeat)}
            onCheckedChange={(v) => setSearch({ repeat: v === true || undefined, page: undefined })}
          />
          {uk.candidates.repeatOnly}
        </label>
      </div>
      <DataTable
        columns={columns}
        data={list.data?.items}
        total={list.data?.total ?? 0}
        state={state}
        onStateChange={(next) => setSearch(tableStateToSearch(next))}
        isLoading={list.isPending}
        getRowId={(r) => r.id}
        onRowClick={(r) => overlays.openCandidate(r.id)}
        empty={
          <EmptyState icon={UsersIcon} title={uk.candidates.empty} hint={uk.candidates.emptyHint} />
        }
      />
    </>
  );
}
